import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { formatApiDateTimeDisplay, formatMenuInstant } from '../lib/date'

function formatDateTime(iso) {
  return formatApiDateTimeDisplay(undefined, iso)
}

async function copyText(text) {
  const value = text ? String(text) : ''
  if (!value) return false
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    // fallback below
  }
  try {
    const input = document.createElement('input')
    input.value = value
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    return true
  } catch {
    return false
  }
}

export default function SupportInquiryDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isOperator = useMemo(() => (user?.roles || []).includes(Roles.OPERATOR), [user?.roles])

  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [data, setData] = useState(null)

  const [reply, setReply] = useState('')
  const [replying, setReplying] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [copiedField, setCopiedField] = useState('')

  useEffect(() => {
    let ignore = false
    async function load() {
      if (!id) return
      setLoading(true)
      setErr('')
      try {
        const res = await api.get(`/inquiries/${id}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) {
          setData(payload)
          setReply(payload?.adminReply || '')
        }
      } catch (e) {
        console.error(e)
        if (!ignore) setErr('문의 내용을 불러오지 못했습니다. (권한/존재 여부 확인)')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [id])

  const onSubmitReply = async () => {
    if (!id) return
    if (!reply.trim()) return
    try {
      setReplying(true)
      await api.post(`/inquiries/${id}/reply`, { reply: reply.trim() })
      // reload
      const res = await api.get(`/inquiries/${id}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setData(payload)
    } catch (e) {
      console.error(e)
      setErr('답글 등록에 실패했습니다.')
    } finally {
      setReplying(false)
    }
  }

  const onConfirm = async () => {
    if (!id) return
    try {
      setConfirming(true)
      await api.post(`/inquiries/${id}/confirm`, { confirm: true })
      const res = await api.get(`/inquiries/${id}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setData(payload)
      try { window?.toast?.show?.('확인 처리되었습니다.', 'success') } catch { }
    } catch (e) {
      console.error(e)
      setErr('확인 처리에 실패했습니다.')
    } finally {
      setConfirming(false)
    }
  }

  const onCopy = async (label, value) => {
    const ok = await copyText(value)
    if (!ok) return
    setCopiedField(label)
    try { window?.toast?.show?.(`${label} 복사됨`, 'success') } catch { }
    try {
      window.clearTimeout(window.__supportInquiryCopyTimer)
      window.__supportInquiryCopyTimer = window.setTimeout(() => setCopiedField(''), 1500)
    } catch {
      // ignore
    }
  }

  if (loading) {
    return <div className="text-sm text-slate-600">불러오는 중…</div>
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">문의 상세</h1>
          <div className="text-xs text-slate-500 mt-1">비공개(작성자/운영자만 열람)</div>
        </div>
        <div className="flex items-center gap-2">
          <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/support/inquiries">목록</Link>
          <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => navigate(-1)}>
            뒤로
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      {data && (
        <div className="rounded border p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <div className="font-semibold">{data.title}</div>
                {data.source === 'LANDING' && (
                  <span className="rounded px-2 py-0.5 border bg-sky-50 border-sky-200 text-sky-700 text-[11px]">
                    랜딩 문의
                  </span>
                )}
              </div>
              {isOperator && data.source === 'LANDING' && (
                <div className="mt-2 rounded border bg-slate-50 p-3 text-sm">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    <div>
                      <div className="text-xs font-semibold text-slate-500">이름</div>
                      <div className="text-slate-800">{data.publicName || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500">연락처</div>
                      <div className="text-slate-800">{data.publicPhone || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500">이메일</div>
                      <div className="text-slate-800">{data.publicEmail || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-slate-500">회사명/소속</div>
                      <div className="text-slate-800">{data.publicCompanyName || '-'}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {data.publicPhone ? (
                      <a className="rounded border bg-white px-3 py-2 text-xs font-medium hover:bg-slate-100" href={`tel:${data.publicPhone}`}>
                        전화걸기
                      </a>
                    ) : null}
                    {data.publicEmail ? (
                      <a className="rounded border bg-white px-3 py-2 text-xs font-medium hover:bg-slate-100" href={`mailto:${data.publicEmail}`}>
                        메일 작성
                      </a>
                    ) : null}
                    {data.publicPhone ? (
                      <button
                        type="button"
                        className="rounded border bg-white px-3 py-2 text-xs font-medium hover:bg-slate-100"
                        onClick={() => onCopy('연락처', data.publicPhone)}
                      >
                        {copiedField === '연락처' ? '연락처 복사됨' : '연락처 복사'}
                      </button>
                    ) : null}
                    {data.publicEmail ? (
                      <button
                        type="button"
                        className="rounded border bg-white px-3 py-2 text-xs font-medium hover:bg-slate-100"
                        onClick={() => onCopy('이메일', data.publicEmail)}
                      >
                        {copiedField === '이메일' ? '이메일 복사됨' : '이메일 복사'}
                      </button>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500">{formatDateTime(data.createdAt)}</div>
          </div>
          {data?.category === 'UW3NN_EXCEPTION' && (
            <div className="rounded border bg-slate-50 p-3 text-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div>
                  <div className="text-xs font-semibold text-slate-500">보험사</div>
                  <div className="text-slate-800">{data.relatedInsurerName || '-'}</div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500">조건</div>
                  <div className="text-slate-800">{data.relatedConditionSummary || '-'}</div>
                </div>
              </div>
              <div className="mt-2">
                <div className="text-xs font-semibold text-slate-500">안내</div>
                <div className="text-slate-800 whitespace-pre-wrap">{data.relatedGuidance || '-'}</div>
              </div>
            </div>
          )}
          <div className="whitespace-pre-wrap text-sm text-slate-800">{data.content}</div>
          <div className="text-xs text-slate-500">
            상태: {data.status === 'ANSWERED' ? '답변완료' : '접수'} · 수정일: {formatDateTime(data.updatedAt)}
            {data?.category === 'UW3NN_EXCEPTION' && (
              <> · 확인: {data.operatorCheckedAt ? `완료(${formatDateTime(data.operatorCheckedAt)})` : '미확인'}</>
            )}
          </div>
          {isOperator && data?.category === 'UW3NN_EXCEPTION' && !data.operatorCheckedAt && (
            <div className="pt-2 border-t">
              <button
                type="button"
                className="rounded bg-emerald-600 text-white px-4 py-2 text-sm disabled:opacity-60"
                disabled={confirming}
                onClick={onConfirm}
              >
                {confirming ? '처리 중…' : '확인'}
              </button>
              <div className="text-xs text-slate-500 mt-2">
                예외질환 문의는 “확인” 버튼을 눌러야 미확인 상태가 해제됩니다.
              </div>
            </div>
          )}
        </div>
      )}

      <div className="rounded border p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-sm">운영자 답글</div>
          {data?.status === 'ANSWERED' ? (
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              답변완료
            </span>
          ) : (
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
              답변대기
            </span>
          )}
        </div>
        {data?.adminReply ? (
          <div className="whitespace-pre-wrap text-sm text-slate-800">{data.adminReply}</div>
        ) : (
          <div className="text-sm text-slate-500">아직 답변이 없습니다.</div>
        )}
        {data?.adminRepliedAt && (
          <div className="text-xs text-slate-500">답변일: {formatDateTime(data.adminRepliedAt)}</div>
        )}

        {isOperator && (
          <div className="pt-2 border-t space-y-2">
            <div className="text-xs text-slate-500">운영자만 답글을 작성/수정할 수 있습니다.</div>
            <textarea
              className="w-full rounded border px-3 py-2 min-h-[140px]"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="답글을 입력하세요"
            />
            <button
              type="button"
              className="rounded bg-brand-navy text-white px-4 py-2 text-sm disabled:opacity-60"
              disabled={replying || !reply.trim()}
              onClick={onSubmitReply}
            >
              {replying ? '등록 중…' : '답글 등록'}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

